import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Star, ChevronUp, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

/* ======= Assets ======= */
import HeroBG from "@/assets/Frame-1.png";
import HeroDevices from "@/assets/MacBook Pro-1.svg";
import HappyWoman from "@/assets/Rectangle 4307.svg";
import DepartmentMockup from "@/assets/emp.png";
import PerformanceMatrix from "@/assets/Heatmap (Weekly).png";
import PayrollGlance from "@/assets/Payroll.png";
import PayrollTrends from "@/assets/Payroll Trends.png";
import LeavePlanner from "@/assets/calendar.png";
import AttendanceAnalysis from "@/assets/Attendance.png";
import FAQ from "@/assets/FAQS.png";
import { motion, Variants } from "framer-motion";

const slideInLeft: Variants = {
  hidden: { opacity: 0, x: -100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInRight: Variants = {
  hidden: { opacity: 0, x: 100 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const slideInBottom: Variants = {
  hidden: { opacity: 0, y: 80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const testimonialContainer: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.18,
      delayChildren: 0.2,
    },
  },
};

const testimonialItem: Variants = {
  hidden: { opacity: 0, y: 50, rotateX: 8, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: "blur(0px)",
    transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
  },
};

const WorkforceManagement = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "Who can use this platform?",
      answer:
        "The platform is designed for HR professionals, managers, and business owners of small, medium, and large organizations who want to streamline HR processes and workforce operations.",
    },
    {
      question: "How do I get started?",
      answer:
        "Sign up for a demo or free trial. Our team will guide you through onboarding and customization to match your organization's needs.",
    },
    {
      question: "Is employee data backed up regularly?",
      answer:
        "Yes, all data is automatically backed up daily with secure cloud storage and encryption. We follow industry-standard compliance practices.",
    },
    {
      question: "Does it support payroll and attendance tracking?",
      answer:
        "Absolutely — full payroll processing, attendance logging, biometric integration support, and real-time tracking are core features.",
    },
    {
      question: "Can managers approve leave online?",
      answer:
        "Yes, managers receive notifications and can approve/reject leaves directly from desktop or mobile with audit trail and notifications.",
    },
  ];

  const testimonials = [
    {
      text: "Managing employees has never felt this easy. Smartborn has incredibly fast onboarding for our HR team.",
      author: "Namith",
      role: "HR Manager",
      rating: 5,
    },
    {
      text: "Smartborn helped us centralize payroll, attendance, and approvals in one place. It saved huge amounts of time and eliminated manual errors.",
      author: "Rahul Chenal",
      role: "Finance Lead",
      rating: 5,
    },
    {
      text: "We finally have real-time visibility into employee data and performance. The platform is smooth, reliable, and efficient.",
      author: "Sahana",
      role: "Operations Manager",
      rating: 5,
    },
  ];

  return (
    <div className="bg-white min-h-screen font-sans antialiased">
      <Navigation />

      {/* ================= HERO ================= */}
      <section
        className="relative bg-black text-white pt-28 md:pt-40 pb-64 md:pb-64 overflow-hidden text-center"
        style={{
          backgroundImage: `url(${HeroBG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-6 relative z-10 max-w-5xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-tight mb-6 tracking-tight">
            Empower people the <span className="text-brand">smarter</span> way
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-white/80 max-w-4xl mx-auto mb-10 md:mb-12 font-light">
            All-in-one workforce management designed to simplify HR while
            empowering teams and ensuring compliance as you scale.
          </p>

          <Button
            size="lg"
            className="bg-brand hover:bg-brand-hover text-base sm:text-lg md:text-xl px-8 sm:px-10 md:px-12 py-6 md:py-7 shadow-lg shadow-primary/30 transition-all"
            asChild
          >
            <Link to="/contact" className="flex items-center gap-3">
              Request Demo <ArrowRight className="h-5 w-5 md:h-6 md:w-6" />
            </Link>
          </Button>
        </div>
      </section>
      <section>
        <div className="-mt-70 md:-mt-60 flex justify-center px-4 relative z-20">
          <motion.div
            className="text-center lg:text-left"
            variants={slideInBottom}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.4 }}
          >
            <img
              src={HeroDevices}
              alt="Smartborn platform on laptop and phone"
              className="max-w-6xl w-full object-contain"
            />
          </motion.div>
        </div>
      </section>

      {/* ================= CLIENTS ================= */}
      {/* <section className="bg-[#EAD1BD] md:pt-10 pb-16 md:pb-24 text-center relative z-10">
        <div className="container mx-auto px-6">
          <p className="text-xl md:text-2xl font-semibold mb-10 md:mb-12 text-gray-800">
            Our clients
          </p>

          <div className="flex flex-wrap justify-center items-center gap-10 sm:gap-12 md:gap-16 lg:gap-20 opacity-80">
            {["Zapier", "Spotify", "Zoom", "Slack", "Amazon", "Adobe"].map(
              (logo) => (
                <div
                  key={logo}
                  className="text-2xl md:text-3xl font-bold text-gray-700 tracking-wide"
                >
                  {logo}
                </div>
              ),
            )}
          </div>
        </div>
      </section> */}

      {/* ================= WE MAKE YOUR BUSINESS BRIGHT ================= */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="order-1 lg:order-1">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                We Make Your Business Bright
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground mb-10 md:mb-12">
                Transform the way your people work together, because empowered
                teams build stronger businesses.
              </p>

              <motion.div
                className="text-center lg:text-left"
                variants={slideInLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <div className="grid grid-cols-2 gap-6 md:gap-10">
                  {[
                    { num: "180+", label: "Projects Done" },
                    { num: "200+", label: "Happy Customers" },
                    { num: "300+", label: "Teams Empowered" },
                    { num: "99%", label: "Satisfaction Rate" },
                  ].map((stat, i) => (
                    <div key={i} className={`border-l-4 ${i % 2 === 0 ? 'border-primary shadow-glow' : 'border-secondary shadow-glow-secondary'} pl-4`}>
                      <div className={`text-4xl md:text-5xl font-extrabold ${i % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'}`}>
                        {stat.num}
                      </div>
                      <div className="text-base md:text-lg text-muted-foreground mt-1">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            <div className="order-2 lg:order-2">
              <motion.div
                className="text-center lg:text-left"
                variants={slideInRight}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.4 }}
              >
                <img
                  src={HappyWoman}
                  alt="Happy team member"
                  className="rounded-3xl shadow-2xl w-full object-cover aspect-[4/3] lg:aspect-auto"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= EVERYTHING YOU NEED ================= */}
      <section className="py-24 md:py-32 bg-gradient-to-b from-[#F5E3D5] via-[#FBE3CF]/30 to-white/30">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-16 md:mb-24">
            Everything You Need{" "}
            <span className="relative inline-block">
              In One Place
              <span className="absolute -bottom-2 left-0 right-0 h-1 bg-brand rounded-full" />
            </span>
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
            {[
              {
                title: "Department Insights",
                desc: "From new hires to seasoned experts, view your entire team in one clean, organized place. Search, sort, and manage employee details without the mess.",
                img: DepartmentMockup,
              },
              {
                title: "Performance Overview",
                desc: "Compare performance in a color-coded view to see who's exceeding targets, performing steadily, or needing attention to guide smarter staffing and performance decisions.",
                img: PerformanceMatrix,
              },
              {
                title: "Payroll at a Glance",
                desc: "View payroll totals, employees paid, and payment status in one consolidated summary, making it easy to track payroll completion and payment timelines.",
                img: PayrollGlance,
              },
              {
                title: "Payroll Cost Trends",
                desc: "Track month-to-month payroll trends and gradual increases, supporting smarter budgeting, forecasting, and financial planning.",
                img: PayrollTrends,
              },
              {
                title: "Leave Planner",
                desc: "View approved, pending, public holiday, and restricted leave dates in a color-coded calendar, helping plan time off clearly while avoiding blocked days.",
                img: LeavePlanner,
              },
              {
                title: "Attendance Analysis",
                desc: "Track patterns of present, absent, and late employees. Identify trends that impact operations, scheduling, and productivity.",
                img: AttendanceAnalysis,
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="flex flex-col rounded-2xl p-6 md:p-8 h-full"
              >
                <div className="flex flex-col min-h-[180px]">
                  <motion.div
                    className="text-center lg:text-left"
                    variants={slideInRight}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                  >
                    <h3 className="text-3xl md:text-4xl font-bold mb-5 md:mb-6 text-primary">
                      {feature.title}
                    </h3>

                    <p className="text-base md:text-lg text-muted-foreground leading-relaxed border-t-2 border-primary/20 pt-5">
                      {feature.desc}
                    </p>
                  </motion.div>
                </div>

                <div className="mt-6">
                  <motion.div
                    className="text-center lg:text-left"
                    variants={slideInBottom}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, amount: 0.4 }}
                  >
                    <img
                      src={feature.img}
                      alt={feature.title}
                      className="w-full object-cover rounded-xl"
                    />
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="py-24 md:py-32 bg-[#FBE3CF]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            What Our Clients Say
          </h2>
          <p className="text-lg text-muted-foreground mb-16 max-w-3xl mx-auto">
            Our business is built on close client relationships, and we're
            grateful to share so many positive experiences with the people we
            serve.
          </p>

          <motion.div
            className="grid md:grid-cols-3 gap-8"
            variants={testimonialContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={testimonialItem}
                className="rounded-2xl bg-white p-8 text-left shadow-md relative"
              >
                <div className="flex mb-4">
                  {Array(t.rating)
                    .fill(0)
                    .map((_, idx) => (
                      <Star
                        key={idx}
                        className={`h-5 w-5 ${idx % 2 === 0 ? 'text-primary shadow-glow fill-primary' : 'text-secondary shadow-glow-secondary fill-secondary'}`}
                      />
                    ))}
                </div>

                <p className="text-foreground mb-6 leading-relaxed">"{t.text}"</p>

                <div>
                  <p className="font-semibold">{t.author}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section
        className="py-28 md:py-36 bg-black text-white relative"
        style={{
          backgroundImage: `url(${FAQ})`,
          backgroundSize: "cover",
          backgroundPosition: "left center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="container mx-auto px-6 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            {/* LEFT SIDE */}
            <div className="pt-6">
              <h2 className="text-4xl md:text-5xl font-medium leading-tight">
                Frequently Asked
                <br />
                Questions
              </h2>
            </div>

            {/* RIGHT SIDE */}
            <div className="border border-white/10 ">
              {faqs.map((faq, index) => {
                const isOpen = openIndex === index;

                return (
                  <div
                    key={index}
                    className="border-b border-white/10 last:border-none"
                  >
                    <button
                      onClick={() => setOpenIndex(isOpen ? null : index)}
                      className="w-full flex justify-between items-center py-6 text-left focus:outline-none focus:ring-0"
                    >
                      <span
                        className={`text-xl md:text-2xl transition-colors duration-300 border-none ${
                          isOpen ? "text-brand" : "text-white"
                        }`}
                      >
                        {faq.question}
                      </span>

                      {isOpen ? (
                        <ChevronUp className="h-6 w-6 text-brand" />
                      ) : (
                        <ChevronDown className="h-6 w-6 text-white" />
                      )}
                    </button>

                    {/* Animated Answer */}
                    <div
                      className={`transition-all duration-300 ease-in-out overflow-hidden ${
                        isOpen
                          ? "max-h-96 opacity-100 pb-6"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <p className="text-gray-300 text-lg leading-relaxed pr-10">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WorkforceManagement;
